/*
 * Copyright 2026 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 */

import i18n.StringKey
import i18n.Strings
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import models.GoalCategory
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLSelectElement
import studio.AgeBand
import studio.HomeOwnership
import studio.IncomeInputMode
import studio.PayFrequency
import studio.ProvinceCode
import studio.RoomKnowledge
import studio.SavingsTenure
import studio.Wiz3rUserProfile
import studio.onboarding.OnboardingFlow
import studio.onboarding.OnboardingStep
import studio.onboarding.PartialWiz3rUserProfile

/**
 * Goal Allocation Studio onboarding stepper — see docs/Goal Allocation Studio Spec §14.
 *
 * One card per question, auto-advances on answer. Renders into a backdrop
 * modal so the host (Goals tab CTA) just calls [show]. On completion, calls
 * [onComplete] after the profile is persisted via ApiService.
 *
 * Wiring:
 *   - The step sequence lives in shared (OnboardingFlow.kt).
 *   - Marginal-rate derivation lives in shared (MarginalRateCalculator.kt).
 *   - This component is the JS-side renderer + state holder.
 */
class WebStudioOnboardingComponent(
    private val apiService: ApiService,
    initial: Wiz3rUserProfile? = null,
    private val onComplete: () -> Unit,
) {
    private var partial: PartialWiz3rUserProfile = initial?.let(OnboardingFlow::fromComplete)
        ?: PartialWiz3rUserProfile()
    private var current: OnboardingStep = OnboardingStep.INCOME
    private var backdrop: HTMLElement? = null
    private var panel: HTMLElement? = null

    fun show() {
        val tm = WebTheme.current
        val bd = document.createElement("div") as HTMLElement
        bd.style.cssText = """
            position: fixed; inset: 0; background: ${if (tm.isDark) "rgba(0,0,0,0.72)" else "rgba(0,0,0,0.35)"};
            z-index: 1100; display: flex; align-items: center; justify-content: center;
            padding: 24px 16px; box-sizing: border-box;
        """.trimIndent()
        val p = document.createElement("div") as HTMLElement
        p.style.cssText = """
            background: ${tm.card}; color: ${tm.textPrimary};
            border-radius: 16px; width: 100%; max-width: 480px;
            padding: 24px 24px 20px; box-shadow: 0 24px 64px rgba(0,0,0,0.25);
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        """.trimIndent()
        bd.appendChild(p)
        document.body?.appendChild(bd)
        bd.addEventListener("click", { ev -> if (ev.target == bd) close() })
        p.addEventListener("click", { ev -> ev.stopPropagation() })
        backdrop = bd
        panel = p
        renderCurrent()
    }

    private fun close() {
        backdrop?.remove()
        backdrop = null
        panel = null
    }

    // -----------------------------------------------------------------------
    // Render dispatch
    // -----------------------------------------------------------------------

    private fun renderCurrent() {
        val tm = WebTheme.current
        val p = panel ?: return
        val total = OnboardingFlow.totalSteps()
        val progressLabel = Strings.format(StringKey.STUDIO_STEPPER_PROGRESS, OnboardingFlow.progressOf(current).toString())
        val backLabel = Strings.get(StringKey.STUDIO_STEPPER_BACK)
        val skipLabel = Strings.get(StringKey.STUDIO_STEPPER_SKIP)
        val backVisible = OnboardingFlow.back(current) != null

        p.innerHTML = """
            <div style="display:flex; align-items:center; justify-content:space-between; margin-bottom:18px;">
                <button id="studioBack" style="
                    background: transparent; border: none; color: ${tm.textSecondary};
                    cursor: pointer; font-size: 14px; padding: 4px 0;
                    visibility: ${if (backVisible) "visible" else "hidden"};
                ">‹ $backLabel</button>
                <div style="
                    color: ${tm.textSecondary}; font-size: 12px;
                    background: ${tm.background}; padding: 4px 10px; border-radius: 999px;
                ">$progressLabel</div>
            </div>
            <div id="studioCardBody"></div>
            <div style="margin-top:24px; padding-top:16px; border-top:1px solid ${tm.border}; text-align:center;">
                <button id="studioSkip" style="
                    background: transparent; border: none; color: ${tm.textSecondary};
                    cursor: pointer; font-size: 13px; padding: 6px 12px;
                    text-decoration: underline;
                ">$skipLabel</button>
            </div>
        """.trimIndent()

        p.querySelector("#studioBack")?.addEventListener("click", {
            val prev = OnboardingFlow.back(current) ?: return@addEventListener
            current = prev
            renderCurrent()
        })
        p.querySelector("#studioSkip")?.addEventListener("click", { goToNextOrFinish() })

        val body = p.querySelector("#studioCardBody") as HTMLElement
        when (current) {
            OnboardingStep.INCOME -> renderIncomeCard(body)
            OnboardingStep.AGE -> renderAgeCard(body)
            OnboardingStep.HOME_OWNERSHIP -> renderHomeCard(body)
            OnboardingStep.RRSP_ROOM -> renderRoomCard(body, isRrsp = true)
            OnboardingStep.TFSA_ROOM -> renderRoomCard(body, isRrsp = false)
            OnboardingStep.SAVINGS_TENURE -> renderTenureCard(body)
            OnboardingStep.GOAL_PRIORITIES -> renderPrioritiesCard(body)
        }
    }

    private fun goToNextOrFinish() {
        val next = OnboardingFlow.advance(current)
        if (next != null) {
            current = next
            renderCurrent()
        } else {
            persistAndFinish()
        }
    }

    private fun persistAndFinish() {
        val tm = WebTheme.current
        val now = kotlin.js.Date().toISOString()
        val complete = OnboardingFlow.toComplete(partial, taxYear = 2025, updatedAt = now)
        if (complete == null) {
            // User skipped the math-essential trio — still show the "All set"
            // confirmation but don't persist (server has nothing to save).
            renderDoneCard()
            return
        }
        GlobalScope.launch {
            try {
                apiService.saveStudioProfile(complete)
            } catch (e: Exception) {
                console.warn("Failed to save Studio profile: ${e.message}")
            }
            renderDoneCard()
        }
    }

    private fun renderDoneCard() {
        val tm = WebTheme.current
        val p = panel ?: return
        p.innerHTML = """
            <div style="text-align:center; padding: 16px 0;">
                <div style="
                    width:56px; height:56px; border-radius:50%;
                    background:${tm.accent}22; color:${tm.accent};
                    display:flex; align-items:center; justify-content:center;
                    margin: 0 auto 16px; font-size:28px;
                ">✓</div>
                <h2 style="margin:0 0 8px; font-size:18px; color:${tm.textPrimary};">
                    ${Strings.get(StringKey.STUDIO_STEPPER_DONE_TITLE)}
                </h2>
                <p style="margin:0 0 24px; color:${tm.textSecondary}; font-size:14px; line-height:1.5;">
                    ${Strings.get(StringKey.STUDIO_STEPPER_DONE_BODY)}
                </p>
                <button id="studioDoneBtn" style="
                    padding: 12px 32px; background:${tm.accent}; color:white;
                    border:none; border-radius:10px; font-size:14px; font-weight:600; cursor:pointer;
                ">${Strings.get(StringKey.STUDIO_STEPPER_DONE_DISMISS)}</button>
            </div>
        """.trimIndent()
        p.querySelector("#studioDoneBtn")?.addEventListener("click", {
            close()
            onComplete()
        })
    }

    // -----------------------------------------------------------------------
    // Card 1 — Income (province + 3 modes; bank-pre-fill is "coming soon")
    // -----------------------------------------------------------------------

    private fun renderIncomeCard(host: HTMLElement) {
        val tm = WebTheme.current
        val mode = partial.incomeInputMode
        host.innerHTML = """
            <h2 style="margin:0 0 14px; font-size:18px; color:${tm.textPrimary};">
                ${Strings.get(StringKey.STUDIO_Q_INCOME_TITLE)}
            </h2>
            ${whyBlock(StringKey.STUDIO_WHY_INCOME)}
            <div style="margin-bottom:16px;">
                <label style="display:block; font-size:12px; color:${tm.textSecondary}; margin-bottom:6px;">
                    ${Strings.get(StringKey.STUDIO_Q_INCOME_PROVINCE_LABEL)}
                </label>
                <select id="studioProvince" style="${selectStyle(tm)}">
                    <option value=""${if (partial.province == null) " selected" else ""} disabled>
                        ${Strings.get(StringKey.STUDIO_Q_INCOME_PROVINCE_PLACEHOLDER)}
                    </option>
                    ${ProvinceCode.entries.joinToString("") { p ->
                        "<option value=\"${p.name}\"${if (partial.province == p) " selected" else ""}>${p.name}</option>"
                    }}
                </select>
            </div>
            <div style="display:flex; gap:8px; margin-bottom:12px;">
                ${modeChip(IncomeInputMode.GROSS_ANNUAL, StringKey.STUDIO_Q_INCOME_MODE_GROSS, mode, tm)}
                ${modeChip(IncomeInputMode.AVERAGE_PAYCHEQUE, StringKey.STUDIO_Q_INCOME_MODE_PAYCHEQUE, mode, tm)}
                ${modeChip(IncomeInputMode.BANK_PRE_FILL, StringKey.STUDIO_Q_INCOME_MODE_BANK, mode, tm, disabled = true)}
            </div>
            <div id="studioIncomeInputs"></div>
            <button id="studioIncomeContinue" style="${primaryBtnStyle(tm)}; margin-top:18px;">
                ${Strings.get(StringKey.STUDIO_Q_PRIORITIES_CONTINUE)}
            </button>
        """.trimIndent()

        // Province change
        (host.querySelector("#studioProvince") as? HTMLSelectElement)?.addEventListener("change", { ev ->
            val v = (ev.target as HTMLSelectElement).value
            if (v.isNotBlank()) partial = partial.copy(province = ProvinceCode.valueOf(v))
        })
        // Mode chips
        host.querySelectorAll(".studio-mode-chip").let { nodes ->
            for (i in 0 until nodes.length) {
                val node = nodes.item(i) as? HTMLElement ?: continue
                node.addEventListener("click", {
                    val newMode = node.getAttribute("data-mode") ?: return@addEventListener
                    if (newMode == IncomeInputMode.BANK_PRE_FILL.name) return@addEventListener  // disabled
                    partial = partial.copy(incomeInputMode = IncomeInputMode.valueOf(newMode))
                    renderIncomeInputs(host)
                })
            }
        }
        // Initial inputs
        renderIncomeInputs(host)
        // Continue button
        host.querySelector("#studioIncomeContinue")?.addEventListener("click", {
            captureIncomeInputs(host)
            if (partial.province == null) return@addEventListener
            val gross = OnboardingFlow.resolveGrossIncome(partial)
            if (gross == null || gross <= 0) return@addEventListener
            partial = partial.copy(grossAnnualIncome = gross)
            goToNextOrFinish()
        })
    }

    private fun renderIncomeInputs(host: HTMLElement) {
        val tm = WebTheme.current
        val container = host.querySelector("#studioIncomeInputs") as? HTMLElement ?: return
        container.innerHTML = when (partial.incomeInputMode) {
            IncomeInputMode.GROSS_ANNUAL -> """
                <div>
                    <label style="display:block; font-size:12px; color:${tm.textSecondary}; margin-bottom:6px;">
                        ${Strings.get(StringKey.STUDIO_Q_INCOME_GROSS_LABEL)}
                    </label>
                    <input id="studioGross" type="number" min="0" step="500" placeholder="80000"
                           value="${partial.grossAnnualIncome?.toLong()?.toString() ?: ""}"
                           style="${inputStyle(tm)}"/>
                </div>
            """.trimIndent()
            IncomeInputMode.AVERAGE_PAYCHEQUE -> """
                <div style="display:flex; gap:12px;">
                    <div style="flex:1;">
                        <label style="display:block; font-size:12px; color:${tm.textSecondary}; margin-bottom:6px;">
                            ${Strings.get(StringKey.STUDIO_Q_INCOME_PAYCHEQUE_LABEL)}
                        </label>
                        <input id="studioPaycheque" type="number" min="0" step="50" placeholder="2500"
                               value="${partial.netPerPeriod?.toLong()?.toString() ?: ""}"
                               style="${inputStyle(tm)}"/>
                    </div>
                    <div style="flex:1;">
                        <label style="display:block; font-size:12px; color:${tm.textSecondary}; margin-bottom:6px;">
                            ${Strings.get(StringKey.STUDIO_Q_INCOME_FREQUENCY_LABEL)}
                        </label>
                        <select id="studioFrequency" style="${selectStyle(tm)}">
                            <option value="MONTHLY"${if (partial.payFrequency == PayFrequency.MONTHLY) " selected" else ""}>${Strings.get(StringKey.STUDIO_Q_INCOME_FREQUENCY_MONTHLY)}</option>
                            <option value="BI_WEEKLY"${if (partial.payFrequency == PayFrequency.BI_WEEKLY) " selected" else ""}>${Strings.get(StringKey.STUDIO_Q_INCOME_FREQUENCY_BI_WEEKLY)}</option>
                        </select>
                    </div>
                </div>
                <div id="studioGrossPreview" style="
                    margin-top:10px; padding:8px 12px; background:${tm.background};
                    border-radius:8px; font-size:12px; color:${tm.textSecondary};
                "></div>
            """.trimIndent()
            IncomeInputMode.BANK_PRE_FILL -> """
                <div style="padding:16px; background:${tm.background}; border-radius:8px;
                            color:${tm.textSecondary}; font-size:13px; text-align:center;">
                    ${Strings.get(StringKey.STUDIO_Q_INCOME_MODE_BANK_COMING_SOON)}
                </div>
            """.trimIndent()
        }
        updateGrossPreview(host)
        // Wire up listeners for the gross-up preview
        if (partial.incomeInputMode == IncomeInputMode.AVERAGE_PAYCHEQUE) {
            (container.querySelector("#studioPaycheque") as? HTMLElement)?.addEventListener("input", {
                captureIncomeInputs(host); updateGrossPreview(host)
            })
            (container.querySelector("#studioFrequency") as? HTMLElement)?.addEventListener("change", {
                captureIncomeInputs(host); updateGrossPreview(host)
            })
        }
    }

    private fun captureIncomeInputs(host: HTMLElement) {
        when (partial.incomeInputMode) {
            IncomeInputMode.GROSS_ANNUAL -> {
                val v = (host.querySelector("#studioGross") as? HTMLInputElement)?.value
                partial = partial.copy(grossAnnualIncome = v?.toDoubleOrNull())
            }
            IncomeInputMode.AVERAGE_PAYCHEQUE -> {
                val net = (host.querySelector("#studioPaycheque") as? HTMLInputElement)?.value?.toDoubleOrNull()
                val freq = (host.querySelector("#studioFrequency") as? HTMLSelectElement)?.value
                partial = partial.copy(
                    netPerPeriod = net,
                    payFrequency = freq?.let { PayFrequency.valueOf(it) } ?: PayFrequency.MONTHLY,
                )
            }
            IncomeInputMode.BANK_PRE_FILL -> {}
        }
    }

    private fun updateGrossPreview(host: HTMLElement) {
        if (partial.incomeInputMode != IncomeInputMode.AVERAGE_PAYCHEQUE) return
        val gross = OnboardingFlow.resolveGrossIncome(partial) ?: return
        val preview = host.querySelector("#studioGrossPreview") as? HTMLElement ?: return
        preview.textContent = Strings.format(StringKey.STUDIO_Q_INCOME_ESTIMATED_GROSS, "\$${gross.toLong()}")
    }

    // -----------------------------------------------------------------------
    // Cards 2-6 — single-select option lists
    // -----------------------------------------------------------------------

    private fun renderAgeCard(host: HTMLElement) {
        renderSingleSelectCard(
            host = host,
            titleKey = StringKey.STUDIO_Q_AGE_TITLE,
            whyKey = StringKey.STUDIO_WHY_AGE,
            options = listOf(
                AgeBand.EIGHTEEN_TO_TWENTYNINE to StringKey.STUDIO_Q_AGE_18_29,
                AgeBand.THIRTY_TO_THIRTYNINE to StringKey.STUDIO_Q_AGE_30_39,
                AgeBand.FORTY_TO_FORTYNINE to StringKey.STUDIO_Q_AGE_40_49,
                AgeBand.FIFTY_TO_FIFTYNINE to StringKey.STUDIO_Q_AGE_50_59,
                AgeBand.SIXTY_TO_SIXTYNINE to StringKey.STUDIO_Q_AGE_60_69,
                AgeBand.SEVENTY_PLUS to StringKey.STUDIO_Q_AGE_70_PLUS,
            ),
            currentSelected = partial.ageBand,
            onPick = { picked -> partial = partial.copy(ageBand = picked); goToNextOrFinish() },
        )
    }

    private fun renderHomeCard(host: HTMLElement) {
        renderSingleSelectCard(
            host = host,
            titleKey = StringKey.STUDIO_Q_HOME_TITLE,
            whyKey = StringKey.STUDIO_WHY_HOME,
            options = listOf(
                HomeOwnership.NEVER_OWNED to StringKey.STUDIO_Q_HOME_NEVER,
                HomeOwnership.OWNED_BEFORE to StringKey.STUDIO_Q_HOME_BEFORE,
                HomeOwnership.DECLINED to StringKey.STUDIO_Q_HOME_DECLINED,
            ),
            currentSelected = partial.homeOwnershipHistory,
            onPick = { picked -> partial = partial.copy(homeOwnershipHistory = picked); goToNextOrFinish() },
        )
    }

    private fun renderRoomCard(host: HTMLElement, isRrsp: Boolean) {
        val tm = WebTheme.current
        val titleKey = if (isRrsp) StringKey.STUDIO_Q_RRSP_ROOM_TITLE else StringKey.STUDIO_Q_TFSA_ROOM_TITLE
        val whyKey = if (isRrsp) StringKey.STUDIO_WHY_RRSP_ROOM else StringKey.STUDIO_WHY_TFSA_ROOM
        val currentKnowledge = if (isRrsp) partial.rrspRoomKnowledge else partial.tfsaRoomKnowledge
        val currentAmount = if (isRrsp) partial.rrspRoomAmount else partial.tfsaRoomAmount
        val showAmount = currentKnowledge == RoomKnowledge.KNOWN || currentKnowledge == RoomKnowledge.ROUGH
        val showCraHelp = currentKnowledge == RoomKnowledge.UNKNOWN
        host.innerHTML = """
            <h2 style="margin:0 0 14px; font-size:18px; color:${tm.textPrimary};">
                ${Strings.get(titleKey)}
            </h2>
            ${whyBlock(whyKey)}
            <div style="display:flex; flex-direction:column; gap:8px;">
                ${roomOption(RoomKnowledge.KNOWN, StringKey.STUDIO_Q_ROOM_KNOWN, currentKnowledge, tm)}
                ${roomOption(RoomKnowledge.ROUGH, StringKey.STUDIO_Q_ROOM_ROUGH, currentKnowledge, tm)}
                ${roomOption(RoomKnowledge.UNKNOWN, StringKey.STUDIO_Q_ROOM_UNKNOWN, currentKnowledge, tm)}
            </div>
            <div id="studioRoomAmountWrap" style="margin-top:12px; display:${if (showAmount) "block" else "none"};">
                <label style="display:block; font-size:12px; color:${tm.textSecondary}; margin-bottom:6px;">
                    ${Strings.get(StringKey.STUDIO_Q_ROOM_AMOUNT_LABEL)}
                </label>
                <input id="studioRoomAmount" type="number" min="0" step="100" placeholder="10000"
                       value="${currentAmount?.toLong()?.toString() ?: ""}"
                       style="${inputStyle(tm)}"/>
            </div>
            <div id="studioRoomCraHelp" style="
                margin-top:12px; padding:14px 16px;
                background:${tm.accent}18; border:1px solid ${tm.accent}55;
                border-radius:10px; display:${if (showCraHelp) "block" else "none"};
            ">
                <p style="margin:0 0 10px; font-size:13px; color:${tm.textPrimary}; line-height:1.5;">
                    ${Strings.get(StringKey.STUDIO_HOW_FIND_ROOM)}
                </p>
                <a href="https://www.canada.ca/en/revenue-agency/services/e-services/digital-services-individuals/account-individuals.html"
                   target="_blank" rel="noopener"
                   style="display:inline-block; color:${tm.accent}; font-size:13px; font-weight:600; text-decoration:none;">
                    ${Strings.get(StringKey.STUDIO_ROOM_CRA_LINK)}
                </a>
                <p style="margin:12px 0 0; font-size:12px; color:${tm.textSecondary}; line-height:1.5;">
                    ${Strings.get(StringKey.STUDIO_ROOM_LEAVE_BLANK_NOTE)}
                </p>
            </div>
            <button id="studioRoomContinue" style="${primaryBtnStyle(tm)}; margin-top:14px;">
                ${Strings.get(StringKey.STUDIO_Q_PRIORITIES_CONTINUE)}
            </button>
        """.trimIndent()
        host.querySelectorAll(".studio-room-option").let { nodes ->
            for (i in 0 until nodes.length) {
                val node = nodes.item(i) as? HTMLElement ?: continue
                node.addEventListener("click", {
                    val v = node.getAttribute("data-room") ?: return@addEventListener
                    val knowledge = RoomKnowledge.valueOf(v)
                    partial = if (isRrsp) partial.copy(rrspRoomKnowledge = knowledge)
                    else partial.copy(tfsaRoomKnowledge = knowledge)
                    renderRoomCard(host, isRrsp)
                })
            }
        }
        host.querySelector("#studioRoomContinue")?.addEventListener("click", {
            val amt = (host.querySelector("#studioRoomAmount") as? HTMLInputElement)?.value?.toDoubleOrNull()
            partial = if (isRrsp) partial.copy(rrspRoomAmount = amt) else partial.copy(tfsaRoomAmount = amt)
            goToNextOrFinish()
        })
    }

    private fun renderTenureCard(host: HTMLElement) {
        renderSingleSelectCard(
            host = host,
            titleKey = StringKey.STUDIO_Q_TENURE_TITLE,
            whyKey = StringKey.STUDIO_WHY_TENURE,
            options = listOf(
                SavingsTenure.NEVER to StringKey.STUDIO_Q_TENURE_NEVER,
                SavingsTenure.FEW_MONTHS to StringKey.STUDIO_Q_TENURE_FEW_MONTHS,
                SavingsTenure.ONE_TO_THREE_YEARS to StringKey.STUDIO_Q_TENURE_ONE_TO_THREE,
                SavingsTenure.FIVE_PLUS to StringKey.STUDIO_Q_TENURE_FIVE_PLUS,
            ),
            currentSelected = partial.savingsTenure,
            onPick = { picked -> partial = partial.copy(savingsTenure = picked); goToNextOrFinish() },
        )
    }

    private fun renderPrioritiesCard(host: HTMLElement) {
        val tm = WebTheme.current
        val opts = listOf(
            GoalCategory.EMERGENCY_FUND to StringKey.STUDIO_Q_PRIORITY_EMERGENCY,
            GoalCategory.HOME_PURCHASE to StringKey.STUDIO_Q_PRIORITY_HOME,
            GoalCategory.RETIREMENT to StringKey.STUDIO_Q_PRIORITY_RETIREMENT,
            GoalCategory.EDUCATION to StringKey.STUDIO_Q_PRIORITY_EDUCATION,
            GoalCategory.OTHER to StringKey.STUDIO_Q_PRIORITY_NONE,
        )
        val selected = partial.goalPriorities.toMutableList()
        host.innerHTML = """
            <h2 style="margin:0 0 8px; font-size:18px; color:${tm.textPrimary};">
                ${Strings.get(StringKey.STUDIO_Q_PRIORITIES_TITLE)}
            </h2>
            <p style="margin:0 0 14px; font-size:12px; color:${tm.textSecondary};">
                ${Strings.get(StringKey.STUDIO_Q_PRIORITIES_HINT)}
            </p>
            ${whyBlock(StringKey.STUDIO_WHY_PRIORITIES)}
            <div style="display:flex; flex-direction:column; gap:8px;">
                ${opts.joinToString("") { (cat, key) ->
                    val isSelected = cat in selected
                    """<div class="studio-priority-option" data-cat="${cat.name}" style="${optionRowStyle(tm, isSelected)}">
                        <span>${Strings.get(key)}</span>
                        <span style="color:${if (isSelected) tm.accent else tm.textSecondary};">${if (isSelected) "✓" else ""}</span>
                    </div>""".trimIndent()
                }}
            </div>
            <button id="studioPrioritiesContinue" style="${primaryBtnStyle(tm)}; margin-top:14px;">
                ${Strings.get(StringKey.STUDIO_Q_PRIORITIES_CONTINUE)}
            </button>
        """.trimIndent()
        host.querySelectorAll(".studio-priority-option").let { nodes ->
            for (i in 0 until nodes.length) {
                val node = nodes.item(i) as? HTMLElement ?: continue
                node.addEventListener("click", {
                    val v = node.getAttribute("data-cat") ?: return@addEventListener
                    val cat = GoalCategory.valueOf(v)
                    val cur = partial.goalPriorities.toMutableList()
                    if (cat in cur) cur.remove(cat) else if (cur.size < 2) cur.add(cat)
                    partial = partial.copy(goalPriorities = cur)
                    renderPrioritiesCard(host)
                })
            }
        }
        host.querySelector("#studioPrioritiesContinue")?.addEventListener("click", { goToNextOrFinish() })
    }

    // -----------------------------------------------------------------------
    // Helpers
    // -----------------------------------------------------------------------

    private fun <T : Enum<*>> renderSingleSelectCard(
        host: HTMLElement,
        titleKey: StringKey,
        whyKey: StringKey,
        options: List<Pair<T, StringKey>>,
        currentSelected: T?,
        onPick: (T) -> Unit,
    ) {
        val tm = WebTheme.current
        host.innerHTML = """
            <h2 style="margin:0 0 14px; font-size:18px; color:${tm.textPrimary};">
                ${Strings.get(titleKey)}
            </h2>
            ${whyBlock(whyKey)}
            <div style="display:flex; flex-direction:column; gap:8px;">
                ${options.joinToString("") { (enumVal, labelKey) ->
                    val isSelected = currentSelected == enumVal
                    """<div class="studio-select-option" data-val="${enumVal.name}" style="${optionRowStyle(tm, isSelected)}">
                        <span>${Strings.get(labelKey)}</span>
                        <span style="color:${if (isSelected) tm.accent else tm.textSecondary};">${if (isSelected) "✓" else ""}</span>
                    </div>""".trimIndent()
                }}
            </div>
        """.trimIndent()
        host.querySelectorAll(".studio-select-option").let { nodes ->
            for (i in 0 until nodes.length) {
                val node = nodes.item(i) as? HTMLElement ?: continue
                node.addEventListener("click", {
                    val v = node.getAttribute("data-val") ?: return@addEventListener
                    val picked = options.first { it.first.name == v }.first
                    onPick(picked)
                })
            }
        }
    }

    private fun whyBlock(key: StringKey): String {
        val tm = WebTheme.current
        return """
            <details style="margin: 0 0 16px;">
                <summary style="cursor:pointer; color:${tm.accent}; font-size:12px; list-style:none;">
                    ${Strings.get(StringKey.STUDIO_WHY_LABEL)}
                </summary>
                <p style="margin:8px 0 0; font-size:12px; color:${tm.textSecondary}; line-height:1.5;">
                    ${Strings.get(key)}
                </p>
            </details>
        """.trimIndent()
    }

    private fun howBlock(key: StringKey): String {
        val tm = WebTheme.current
        return """
            <details style="margin: 0 0 16px;">
                <summary style="cursor:pointer; color:${tm.accent}; font-size:12px; list-style:none;">
                    ${Strings.get(StringKey.STUDIO_HOW_LABEL)}
                </summary>
                <p style="margin:8px 0 0; font-size:12px; color:${tm.textSecondary}; line-height:1.5;">
                    ${Strings.get(key)}
                </p>
            </details>
        """.trimIndent()
    }

    private fun modeChip(mode: IncomeInputMode, labelKey: StringKey, selected: IncomeInputMode, tm: theme.ThemeColors, disabled: Boolean = false): String {
        val isSelected = mode == selected
        val opacity = if (disabled) "opacity: 0.55;" else ""
        val tooltip = if (disabled) "title=\"${Strings.get(StringKey.STUDIO_Q_INCOME_MODE_BANK_COMING_SOON)}\"" else ""
        return """
            <div class="studio-mode-chip" data-mode="${mode.name}" $tooltip style="
                flex:1; padding:10px; text-align:center;
                background:${if (isSelected) tm.accent + "22" else "transparent"};
                color:${if (isSelected) tm.accent else tm.textPrimary};
                border: 1px solid ${if (isSelected) tm.accent else tm.border};
                border-radius:10px; cursor:${if (disabled) "not-allowed" else "pointer"};
                font-size:12px; font-weight:500;
                $opacity
            ">${Strings.get(labelKey)}</div>
        """.trimIndent()
    }

    private fun roomOption(value: RoomKnowledge, labelKey: StringKey, current: RoomKnowledge?, tm: theme.ThemeColors): String {
        val isSelected = current == value
        return """<div class="studio-room-option" data-room="${value.name}" style="${optionRowStyle(tm, isSelected)}">
            <span>${Strings.get(labelKey)}</span>
            <span style="color:${if (isSelected) tm.accent else tm.textSecondary};">${if (isSelected) "✓" else ""}</span>
        </div>""".trimIndent()
    }

    private fun optionRowStyle(tm: theme.ThemeColors, selected: Boolean): String = """
        display:flex; justify-content:space-between; align-items:center;
        padding:14px 16px;
        background:${if (selected) tm.accent + "18" else tm.background};
        color:${tm.textPrimary};
        border:1px solid ${if (selected) tm.accent else tm.border};
        border-radius:10px; cursor:pointer; font-size:14px;
        transition: background 0.15s, border-color 0.15s;
    """.trimIndent()

    private fun primaryBtnStyle(tm: theme.ThemeColors): String = """
        width:100%; padding:12px; background:${tm.accent}; color:white;
        border:none; border-radius:10px; font-size:14px; font-weight:600; cursor:pointer;
    """.trimIndent()

    private fun selectStyle(tm: theme.ThemeColors): String = """
        width:100%; padding:10px; background:${tm.background};
        border:1px solid ${tm.border}; border-radius:8px; color:${tm.textPrimary};
        font-size:14px; cursor:pointer;
    """.trimIndent()

    private fun inputStyle(tm: theme.ThemeColors): String = """
        width:100%; padding:10px; box-sizing:border-box;
        background:${tm.background}; border:1px solid ${tm.border};
        border-radius:8px; color:${tm.textPrimary}; font-size:14px;
    """.trimIndent()
}
